/**
 * An agent's reply, as Markdown (C§6.5). A link to a page in this app (a doc,
 * a task, a chat) opens here: a doc as its mention chip, a task, a chat or an
 * Area as its title, never its full address. A link to another site asks before it opens,
 * which is Streamdown's own confirmation. A construct cut short (a reply
 * still streaming, or a receipt's first 600 characters) is closed, so it
 * isn't shown raw. The chat and a receipt's "What it said" both render
 * replies with it.
 */
import { Link } from "@crew/ui/components/link";
import { cn } from "@crew/ui/lib/utils";
import { Check, Copy, ExternalLink, X } from "lucide-react";
import {
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentProps,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { Streamdown, StreamdownContext, type LinkSafetyModalProps } from "streamdown";
import { DocChip } from "../areas/mention-parts";
import { appItem, appPageHref, pageOrigin, type AppItem } from "./crew-link";
import { MarkdownTable } from "./markdown-table";

const LINK_CLASS = "wrap-anywhere font-medium text-primary underline";

/** A page in this app: a real link, so it navigates here. */
function AppPageLink({
  to,
  className,
  title,
  children,
}: {
  to: string;
  className?: string;
  title?: string;
  children: ComponentProps<"a">["children"];
}) {
  return (
    <Link href={to} title={title} className={cn(LINK_CLASS, className)} data-streamdown="link">
      {children}
    </Link>
  );
}

/** The titles a reply's links can show: the thread's docs and tasks by id. Ids are lowercase. */
export type ReplyTitles = {
  docs?: ReadonlyMap<string, { title: string }>;
  tasks?: ReadonlyMap<string, { title: string }>;
};

const TitlesContext = createContext<ReplyTitles>({});

/** What a link with no title known says. */
const OPEN: Record<AppItem["kind"], string> = {
  doc: "Open doc",
  task: "Open task",
  chat: "Open chat",
  area: "Open area",
};

/** The words a link's children show, through any formatting (`[**Garage gym**](…)`). */
function textOf(children: ReactNode): string {
  if (typeof children === "string" || typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(textOf).join("");
  if (isValidElement<{ children?: ReactNode }>(children)) return textOf(children.props.children);
  return "";
}

/** A link's own words, unless they're only its address (a bare URL, or `[url](url)`). */
function ownWords(children: ReactNode): string | null {
  const text = textOf(children).trim();
  if (!text) return null;
  return /^(?:https?:\/\/|www\.|\/hq(?:\/|$))/i.test(text) ? null : text;
}

/**
 * A link to one item in the app: a doc as its mention chip, any other item as
 * its title. The title is the thread's, else the link's own words, else
 * "Open doc" and the like: never the address.
 */
function AppItemLink({
  to,
  item,
  title,
  children,
}: {
  to: string;
  item: AppItem;
  title?: string;
  children: ReactNode;
}) {
  const titles = useContext(TitlesContext);
  const known = (
    item.kind === "doc" ? titles.docs : item.kind === "task" ? titles.tasks : null
  )?.get(item.id.toLowerCase())?.title;
  const label = known ?? ownWords(children) ?? OPEN[item.kind];
  if (item.kind === "doc") return <DocChip mention={{ id: item.id, title: label }} />;
  return (
    <AppPageLink to={to} title={title}>
      {label}
    </AppPageLink>
  );
}

const ACTION =
  "flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2 font-medium text-sm transition-all";

/** "Copied" fades after a moment. A refused clipboard leaves the button as it was. */
function useCopied(url: string) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // The button stays "Copy link" when the clipboard is refused.
    }
  }, [url]);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);
  return { copied, copy };
}

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>("button, a[href]")].filter(
    (el) => !el.hasAttribute("disabled"),
  );
}

function edgeButton(items: HTMLElement[], shiftKey: boolean): HTMLElement | undefined {
  return shiftKey ? items[0] : items[items.length - 1];
}

function destination(items: HTMLElement[], shiftKey: boolean): HTMLElement {
  return shiftKey ? items[items.length - 1]! : items[0]!;
}

/** True while Tab is still moving between the confirmation's buttons. */
function staysPut(active: EventTarget | null, root: HTMLElement, edge: HTMLElement): boolean {
  if (!(active instanceof HTMLElement)) return false;
  if (active === root || active === edge) return false;
  return root.contains(active);
}

/** Tab stays on the confirmation's buttons. */
function keepFocusInside(event: KeyboardEvent, root: HTMLElement) {
  if (event.key !== "Tab") return;
  const items = focusables(root);
  const edge = edgeButton(items, event.shiftKey);
  if (!edge || staysPut(document.activeElement, root, edge)) return;
  event.preventDefault();
  destination(items, event.shiftKey).focus();
}

/** Hold the page still. Focus stays in the confirmation, and returns when it closes. */
function useHoldPage(isOpen: boolean, onClose: () => void, dialog: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const returnTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      else if (dialog.current) keepFocusInside(event, dialog.current);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
      returnTo?.focus();
    };
  }, [dialog, isOpen, onClose]);
}

function ModalActions({
  copied,
  onCopy,
  onConfirm,
}: {
  copied: boolean;
  onCopy: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="flex gap-2">
      <button
        type="button"
        className={cn(ACTION, "border bg-background hover:bg-muted")}
        onClick={onCopy}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
        <span>{copied ? "Copied" : "Copy link"}</span>
      </button>
      <button
        type="button"
        className={cn(ACTION, "bg-primary text-primary-foreground hover:bg-primary/90")}
        onClick={onConfirm}
      >
        <ExternalLink size={14} />
        <span>Open link</span>
      </button>
    </div>
  );
}

/**
 * Streamdown's confirmation, for a link that leaves the app. The same words
 * and layout as its default modal: replacing the link component is what lets
 * an in-app page skip it, and this keeps the question for every other link.
 */
function OutsideLinkModal({ url, isOpen, onClose, onConfirm }: LinkSafetyModalProps) {
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const { copied, copy } = useCopied(url);
  useHoldPage(isOpen, onClose, dialog);
  if (!isOpen || typeof document === "undefined") return null;
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/50 backdrop-blur-sm"
      data-streamdown="link-safety-modal"
      onClick={onClose}
    >
      <div
        ref={dialog}
        className="relative mx-4 flex w-full max-w-md flex-col gap-4 rounded-xl border bg-background p-6 shadow-lg outline-none"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <button
          type="button"
          className="absolute top-4 right-4 rounded-md p-1 text-muted-foreground transition-all hover:bg-muted hover:text-foreground"
          aria-label="Close"
          onClick={onClose}
        >
          <X size={16} />
        </button>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 text-lg font-semibold">
            <ExternalLink size={20} />
            <span id={titleId}>Open external link?</span>
          </div>
          <p className="text-sm text-muted-foreground">
            You're about to visit an external website.
          </p>
        </div>
        <div
          className={cn(
            "break-all rounded-md bg-muted p-3 font-mono text-sm",
            url.length > 100 && "max-h-32 overflow-y-auto",
          )}
        >
          {url}
        </div>
        <ModalActions
          copied={copied}
          onCopy={() => void copy()}
          onConfirm={() => {
            onConfirm();
            onClose();
          }}
        />
      </div>
    </div>,
    document.body,
  );
}

function OutsideLink({
  href,
  children,
  className,
  title,
}: {
  href?: string;
  children: ComponentProps<"a">["children"];
  className?: string;
  title?: string;
}) {
  const { linkSafety } = useContext(StreamdownContext);
  const [open, setOpen] = useState(false);
  const incomplete = href === "streamdown:incomplete-link";
  const onClick = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      if (!linkSafety?.enabled || !href || incomplete) return;
      event.preventDefault();
      if (linkSafety.onLinkCheck && (await linkSafety.onLinkCheck(href))) {
        window.open(href, "_blank", "noreferrer");
        return;
      }
      setOpen(true);
    },
    [href, incomplete, linkSafety],
  );
  const onClose = useCallback(() => setOpen(false), []);
  const onConfirm = useCallback(() => {
    if (href) window.open(href, "_blank", "noreferrer");
  }, [href]);
  if (!(linkSafety?.enabled && href)) {
    return (
      <a
        className={cn(LINK_CLASS, className)}
        data-incomplete={incomplete || undefined}
        data-streamdown="link"
        href={href}
        rel="noreferrer"
        target="_blank"
        title={title}
      >
        {children}
      </a>
    );
  }
  const modal = { url: href, isOpen: open, onClose, onConfirm };
  return (
    <>
      <button
        type="button"
        className={cn(LINK_CLASS, "appearance-none text-left", className)}
        data-incomplete={incomplete || undefined}
        data-streamdown="link"
        onClick={(event) => void onClick(event)}
        title={title}
      >
        {children}
      </button>
      {linkSafety.renderModal ? linkSafety.renderModal(modal) : <OutsideLinkModal {...modal} />}
    </>
  );
}

function ReplyLink({ href, children, className, title }: ComponentProps<"a">) {
  const to = appPageHref(href, pageOrigin());
  const item = to ? appItem(to) : null;
  if (to && item) {
    return (
      <AppItemLink to={to} item={item} title={title}>
        {children}
      </AppItemLink>
    );
  }
  if (to) {
    return (
      <AppPageLink to={to} className={className} title={title}>
        {children}
      </AppPageLink>
    );
  }
  return (
    <OutsideLink href={href} className={className} title={title}>
      {children}
    </OutsideLink>
  );
}

/** A table keeps its columns and scrolls sideways on a phone, as a doc's does. */
const COMPONENTS = { a: ReplyLink, table: MarkdownTable };

export function ReplyMarkdown({
  children,
  streaming = false,
  className,
  titles = NO_TITLES,
}: {
  children: string;
  streaming?: boolean;
  className?: string;
  /** The thread's docs and tasks, for the titles of links to them. */
  titles?: ReplyTitles;
}) {
  return (
    <TitlesContext.Provider value={titles}>
      <Streamdown
        className={className}
        components={COMPONENTS}
        isAnimating={streaming}
        controls={false}
      >
        {children}
      </Streamdown>
    </TitlesContext.Provider>
  );
}

const NO_TITLES: ReplyTitles = {};

/**
 * A reply quoted in a narrow panel (a receipt): headings no bigger than the
 * panel's own title, and a long URL or word wraps rather than widening it.
 */
export const QUOTED_REPLY = cn(
  "min-w-0 space-y-2.5 [overflow-wrap:anywhere]",
  "[&_h1]:mt-3 [&_h1]:mb-1 [&_h1]:text-[15px] [&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:text-[15px]",
  "[&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:text-body [&_h4]:text-body [&_h5]:text-body [&_h6]:text-body",
  "[&_li]:py-0.5 [&_ol]:pl-1 [&_ul]:pl-1",
);
